/**
 * Heavy `POLYLINE` × TRIM / EXTEND — the ONE truth for "which heavy polyline
 * this command can act on, in which role, and what its drawn 2D chain is".
 *
 * `@codex PR #785 round 4, P2` ("Support heavy polylines in TRIM and EXTEND"):
 * after `Polyline` joined `DrawingEntity`, the TRIM/EXTEND pipeline still knew
 * only `LWPOLYLINE` — `computeTrimSurvivors` / `computeExtendResult` fell
 * through their `default: return null` arm for a heavy target and
 * `entityToPolyline` returned `null` for a heavy cutting/boundary edge. Both
 * failures are SILENT: the user picks the polyline, the command reports
 * success and nothing moves. CLAUDE.md ПРИОРИТЕТ #1 forbids exactly that
 * half-admitted state for a primitive the model already carries.
 *
 * This module is the ROLE GATE both halves share. The chain projection it
 * returns is `./heavyPolylineDrawnChain.ts`, which in turn reads `cad-core`'s
 * `heavyPolylineRuns` — the topology one-truth for WHICH records are visible,
 * in what order, and which spans are curved — rather than growing a sixth
 * independent unpacker of group 70 / VERTEX bit 8. (That projection moved down
 * into its own leaf in @codex PR #785 round 14, when `geometry/offset-entity`
 * and `geometry/create-arraypath` needed the identical walk under their own,
 * different eligibility rules; TRIM's behaviour is unchanged.)
 *
 * ## What AutoCAD accepts (Autodesk TRIM / EXTEND command reference)
 *
 * - cutting / boundary EDGES: 2D and 3D polylines (plus arcs, circles,
 *   ellipses, lines, rays, regions, splines, text, xlines, layout viewports).
 * - objects that can be TRIMMED / EXTENDED: arcs, circles, elliptical arcs,
 *   lines, 2D and 3D polylines, rays, splines.
 * - polygon meshes and poly-face meshes appear in NEITHER list: a mesh is a
 *   surface, edited by the mesh commands, and it has no single 2D path to cut
 *   against. `cad-core`'s `heavyPolylineRuns` returns MANY runs for one (rows,
 *   columns, face edges), so there is not even a single chain to hand the
 *   intersector.
 *
 * ## Which of those this pipeline can honour today
 *
 * | subtype (group 70)      | EDGE | TARGET |
 * |-------------------------|------|--------|
 * | 2D polyline             | yes  | yes    |
 * | 2D polyline, curve/spline-fit | yes | REFUSED (`"fit"`) |
 * | 3D polyline             | yes  | REFUSED (`"polyline-3d"`) |
 * | polygon / poly-face mesh | REFUSED (`"mesh"`) | REFUSED (`"mesh"`) |
 *
 * The two TARGET refusals are KNOWN-INCOMPLETE SUBSYSTEMS, documented here
 * with their completion plan and reported to the user as a warning-tone audit
 * row — CLAUDE.md ПРИОРИТЕТ #1's one allowed exception, the same shape
 * `./tableScaleRefusal.ts` uses for `geometry/scale-entities` × `Table`:
 *
 * - **`"polyline-3d"`** — every span of the TRIM/EXTEND contour is 2D
 *   (`IntPoint` is `{x, y}`; `trimPolylineTarget` parameterises a chain as
 *   `segment index + local-t` in the XY projection). A 3D polyline's per-vertex
 *   Z is REAL WCS data (`heavyPolylineWcsResolver`'s own rule), so a freshly
 *   cut vertex needs its Z interpolated along the 3D segment and an EXTENDed
 *   terminal vertex needs its Z EXTRAPOLATED along it. Writing the survivors
 *   back through the 2D contour would silently flatten those elevations — the
 *   same class of defect as trimming a bulged span at its chord. As an EDGE a
 *   3D polyline is fully supported: an edge only CONTRIBUTES crossing points
 *   and mutates nothing, and the XY projection is exactly what AutoCAD's own
 *   default `PROJMODE = 1` (project onto the current UCS XY plane) uses.
 *   COMPLETING IT: give `PolylineTargetResult` a per-vertex Z channel fed by
 *   the same param the widths already interpolate on, plus a terminal-segment
 *   Z extrapolation in `extendPolylineTarget`, then delete this arm.
 * - **`"fit"`** — a CURVE-fit (bit 2) or SPLINE-fit (bit 4) polyline's record
 *   run is the OUTPUT of a fitting pass. AutoCAD re-fits the remainder when
 *   such a polyline is trimmed; this codebase has no polyline fitter, and a
 *   hand-computed cut vertex spliced into a fitted run produces a record set
 *   that is not the output of any fit while group 70 still claims it is (and,
 *   for a spline-fit polyline, writing back only the VISIBLE run would DELETE
 *   the bit-16 control FRAME records outright). As an EDGE both are fully
 *   supported — the drawn-chain leaf already resolves the drawn approximation,
 *   which is exactly the curve a cutting edge must cross.
 *   COMPLETING IT: a polyline curve/spline fitter (PEDIT Fit / Spline) that
 *   regenerates the run — and the frame — from the surviving control vertices,
 *   wired so the refit and the vertex write land in ONE `EntityMutation`.
 */

import {
  polylineSubtype,
  type DrawingSpace,
  type PolylineEntity,
} from "@dxf-ai-editor/drawing-model";

import {
  heavyPolylineDrawnChain,
  isHeavyPolylineFitted,
  type HeavyPolylineDrawnChain,
} from "./heavyPolylineDrawnChain";

/**
 * The `{x, y, bulge}` chain vertex, re-exported from the leaf that now owns the
 * projection (`./heavyPolylineDrawnChain.ts`) so this module's existing
 * consumers — `./heavyPolylineTrimTarget.ts`, `./trimExtendIntersection.ts` —
 * keep their import path unchanged (CLAUDE.md ПРИОРИТЕТ #2: a moved symbol
 * keeps a re-export at its old home).
 */
export type { HeavyPolylineChainVertex } from "./heavyPolylineDrawnChain";

/**
 * The two roles a heavy polyline can play in TRIM / EXTEND. They are SEPARATE
 * code paths with separate eligibility (see the table in the module header),
 * so the predicate takes the role rather than answering one question for both.
 */
export type HeavyPolylineTrimRole = "edge" | "target";

/**
 * Why TRIM / EXTEND declines this heavy polyline in this role. `null` (from
 * {@link heavyPolylineTrimRefusal}) means it is fully supported.
 */
export type HeavyPolylineTrimRefusal = "mesh" | "polyline-3d" | "fit";

/**
 * `null` when TRIM / EXTEND fully supports `entity` in `role`, otherwise the
 * reason it is declined. See the module header for each reason's rationale and
 * completion plan.
 *
 * Total and pure — the SINGLE predicate the geometry reducer's guard and the
 * shell reducer's audit message both call, so the two can never drift into
 * "reported a refusal the document did not make" or "stayed silent about one
 * it did" (the two-site shape `./tableScaleRefusal.ts` established).
 */
export function heavyPolylineTrimRefusal(
  entity: Pick<PolylineEntity, "flags">,
  role: HeavyPolylineTrimRole,
): HeavyPolylineTrimRefusal | null {
  const subtype = polylineSubtype(entity);
  // A mesh is declined in BOTH roles, so this test comes first and is
  // role-blind.
  if (subtype === "polygon-mesh" || subtype === "polyface-mesh") return "mesh";
  // Tested POSITIVELY (`=== "target"`), not as "anything that is not an edge":
  // the two extra refusals below are TARGET-only, and stating which role owns
  // them keeps the predicate honest if a third role is ever added.
  if (role === "target") {
    if (subtype === "3d") return "polyline-3d";
    if (isHeavyPolylineFitted(entity)) return "fit";
  }
  return null;
}

/**
 * The heavy polyline's drawn chain, or `null` when TRIM / EXTEND declines it in
 * `role` (see {@link heavyPolylineTrimRefusal}) or the visible run is too short
 * to form a segment.
 *
 * The projection itself — which records draw, in what order, with which bulges,
 * and the record indices a write-back needs — is
 * {@link heavyPolylineDrawnChain}, the ONE such walk this package has, shared
 * with `geometry/offset-entity` and `geometry/create-arraypath`
 * (`./heavyPolylineCurveRefusal.ts`). Only the ROLE GATE above is TRIM's own:
 * the two commands genuinely disagree about which subtypes they accept (a 3D
 * polyline is a perfectly good cutting edge and not a valid OFFSET target), so
 * the eligibility stays per-command while the geometry is shared.
 */
export function heavyPolylineChain(
  entity: PolylineEntity,
  role: HeavyPolylineTrimRole,
): HeavyPolylineDrawnChain | null {
  if (heavyPolylineTrimRefusal(entity, role) !== null) return null;
  return heavyPolylineDrawnChain(entity);
}

/** What a `geometry/trim-entity` / `geometry/extend-entity` dispatch will
 *  actually decline in its target and cutting/boundary-edge sets. */
export interface HeavyPolylineTrimRefusalSummary {
  /**
   * Why the TARGET is declined, or `null` when it is supported (including when
   * the target is not a heavy polyline at all, or resolves to no entity).
   */
  readonly targetRefusal: HeavyPolylineTrimRefusal | null;
  /**
   * Ids of the cutting / boundary EDGES the command declines, in document
   * order. Only the mesh subtypes land here — every other polyline is a valid
   * edge — so a non-empty list always means "a mesh was picked as an edge".
   */
  readonly refusedEdgeIds: readonly string[];
}

/**
 * Resolve a TRIM / EXTEND dispatch's target and edge ids against `spaces` and
 * report what the command declines, using {@link heavyPolylineTrimRefusal} as
 * the single predicate.
 *
 * ONE document walk. An id that matches no entity counts as neither declined
 * nor accepted — an unresolvable id is not something the command refused.
 * The target id is excluded from the edge tally because the TRIM/EXTEND
 * reducers already skip an edge whose id equals the target's (`if (edge.id ===
 * entityId) continue`), so counting it would report a refusal the document
 * never made.
 */
export function summarizeHeavyPolylineTrimRefusal(
  spaces: readonly DrawingSpace[],
  targetId: string,
  edgeIds: readonly string[],
): HeavyPolylineTrimRefusalSummary {
  const wantedEdges = new Set(edgeIds);
  wantedEdges.delete(targetId);
  let targetRefusal: HeavyPolylineTrimRefusal | null = null;
  const refusedEdgeIds: string[] = [];
  for (const space of spaces) {
    for (const entity of space.entities) {
      // `wantedEdges.delete` runs for EVERY entity, heavy or not: it is both
      // the membership test and the de-duplicator, so an id resolved to a Line
      // is struck off exactly once and a repeated id is counted once — the same
      // single-walk shape `./tableScaleRefusal.ts` uses. Only a heavy POLYLINE
      // can be declined, so the type test gates the predicate rather than
      // short-circuiting the walk.
      const isHeavy = entity.type === "Polyline";
      if (isHeavy && entity.id === targetId) {
        targetRefusal = heavyPolylineTrimRefusal(entity, "target");
      }
      const wanted = wantedEdges.delete(entity.id);
      // EQUIVALENT MUTANT (targeted-cad-mutation-testing's equivalent-mutant
      // contract), StringLiteral `"edge"` -> `""`: `heavyPolylineTrimRefusal`
      // tests the role POSITIVELY, `role === "target"`, and the only refusal
      // outside that branch is the role-blind mesh test. Every string that is
      // not `"target"` therefore takes the identical path — `""` and `"edge"`
      // return the same value for every entity, so no test can discriminate
      // them. `HeavyPolylineTrimRole` makes `""` unreachable in real code; only
      // a mutant can produce it.
      //
      // NOTE: the directive must sit DIRECTLY above the mutated string's OWN
      // line, not above the enclosing multi-line `if` — Stryker resolves
      // "next-line" against the mutated expression's `loc.start.line` (the
      // same trap `./trimExtendPolylineTarget.ts` documents).
      if (
        wanted &&
        isHeavy &&
        // Stryker disable next-line StringLiteral: the predicate branches on `role === "target"` only, so every non-"target" string behaves identically to "edge" (see equivalent-mutant note above)
        heavyPolylineTrimRefusal(entity, "edge") !== null
      ) {
        refusedEdgeIds.push(entity.id);
      }
    }
  }
  return { targetRefusal, refusedEdgeIds };
}
